import { Container, Graphics, Sprite, Texture } from 'pixi.js';

import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import type { RuntimeBranding } from '../../game/runtimeConfig';
import type { GameWorldSnapshot } from '../world/types';

interface BannerView {
  readonly root: Container;
  readonly cloth: Graphics;
  readonly crest: Graphics;
  readonly logo: Sprite;
}

const BANNER_POSITIONS = [
  { x: 14.65, y: 8.35 },
  { x: 17.35, y: 8.35 },
  { x: 15.95, y: 10.15 },
] as const;

/**
 * Adds live kingdom identity above the baked voxel scenery. This keeps flags
 * recolourable without regenerating every stage asset for every streamer.
 */
export class RealmBrandingRenderer {
  private readonly banners: BannerView[];
  private readonly primary: number;
  private readonly secondary: number;
  private readonly logoEnabled: boolean;

  constructor(layer: Container, branding: RuntimeBranding) {
    this.primary = hexToNumber(branding.primaryColor, 0xd6a84f);
    this.secondary = hexToNumber(branding.secondaryColor, 0x4f8fd6);
    this.logoEnabled = branding.showLogoOnBanners && Boolean(branding.logoUrl);
    this.banners = BANNER_POSITIONS.map((_, index) => this.createBanner(layer, branding.logoUrl, index));
  }

  render(snapshot: GameWorldSnapshot): void {
    const count = snapshot.progressionLevel >= 4 ? 3 : snapshot.progressionLevel >= 2 ? 2 : 1;
    this.banners.forEach((banner, index) => {
      banner.root.visible = index < count;
      if (!banner.root.visible) return;
      const position = BANNER_POSITIONS[index];
      const x = position.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2;
      const y = position.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2;
      banner.root.position.set(Math.round(x), Math.round(y));
      banner.root.zIndex = Math.floor(y + 34);
    });
  }

  private createBanner(layer: Container, logoUrl: string, index: number): BannerView {
    const root = new Container();
    const pole = new Graphics();
    const cloth = new Graphics();
    const crest = new Graphics();
    let logo = new Sprite(Texture.EMPTY);

    this.drawVoxelPole(pole);
    this.drawVoxelCloth(cloth);
    this.drawPixelCrest(crest);

    if (this.logoEnabled) {
      try {
        logo = Sprite.from(logoUrl);
        logo.anchor.set(0.5);
        logo.position.set(26, -51);
        logo.width = 18;
        logo.height = 18;
        logo.roundPixels = true;
        logo.texture.source.scaleMode = 'nearest';
      } catch {
        logo = new Sprite(Texture.EMPTY);
        crest.visible = true;
      }
    }
    logo.eventMode = 'none';
    root.label = `Kingdom Banner ${index + 1}`;
    root.eventMode = 'none';
    root.addChild(pole, cloth, crest, logo);
    layer.addChild(root);
    return { root, cloth, crest, logo };
  }

  private drawVoxelPole(graphics: Graphics): void {
    const front = 0x805426;
    const side = 0x4b2b12;
    const top = 0xc28b43;
    for (let row = 0; row < 12; row += 1) {
      const y = -72 + row * 6;
      graphics.rect(-3, y, 6, 6).fill(front);
      graphics.poly([3, y, 6, y - 2, 6, y + 4, 3, y + 6]).fill(side);
      graphics.poly([-3, y, 0, y - 2, 6, y - 2, 3, y]).fill(top);
    }
    graphics.rect(-5, -78, 8, 6).fill(0xd5a94f);
    graphics.rect(3, -80, 4, 8).fill(0x8f5e1c);
    graphics.rect(-2, -82, 6, 4).fill(0xf0cf72);
  }

  private drawVoxelCloth(graphics: Graphics): void {
    const rowLengths = [8, 8, 7, 6, 7];
    const cellSize = 6;
    const startX = 5;
    const startY = -70;
    const primaryLight = shadeColor(this.primary, 24);
    const primaryDark = shadeColor(this.primary, -26);
    const secondaryDark = shadeColor(this.secondary, -30);

    rowLengths.forEach((length, row) => {
      for (let column = 0; column < length; column += 1) {
        const x = startX + column * cellSize;
        const y = startY + row * cellSize;
        const edge = row === 0 || row === rowLengths.length - 1 || column === 0 || column === length - 1;
        const color = edge
          ? this.secondary
          : (row + column) % 3 === 0
            ? primaryLight
            : this.primary;
        graphics.rect(x, y, cellSize, cellSize).fill(color);
        graphics.rect(x, y + cellSize - 1, cellSize, 1).fill(edge ? secondaryDark : primaryDark);
        graphics.rect(x + cellSize - 1, y, 1, cellSize).fill(primaryDark);
        if (!edge && (row + column) % 4 === 0) {
          graphics.rect(x + 1, y + 1, 2, 1).fill({ color: 0xffffff, alpha: 0.22 });
        }
      }
    });
  }

  private drawPixelCrest(graphics: Graphics): void {
    const gold = 0xffe69a;
    const shadow = 0x9a6419;
    const x = 18;
    const y = -59;
    graphics.rect(x, y, 4, 4).fill(gold);
    graphics.rect(x + 8, y - 4, 4, 8).fill(gold);
    graphics.rect(x + 16, y, 4, 4).fill(gold);
    graphics.rect(x + 2, y + 4, 16, 8).fill(gold);
    graphics.rect(x + 2, y + 12, 16, 4).fill(shadow);
    graphics.rect(x + 5, y + 6, 3, 3).fill(this.primary);
    graphics.rect(x + 12, y + 6, 3, 3).fill(this.primary);
  }
}

function hexToNumber(value: string, fallback: number): number {
  return /^#[0-9a-f]{6}$/i.test(value) ? Number.parseInt(value.slice(1), 16) : fallback;
}

function shadeColor(color: number, amount: number): number {
  const red = Math.max(0, Math.min(255, (color >> 16) + amount));
  const green = Math.max(0, Math.min(255, ((color >> 8) & 0xff) + amount));
  const blue = Math.max(0, Math.min(255, (color & 0xff) + amount));
  return (red << 16) | (green << 8) | blue;
}
